const uploadErrorKey = (key: string) => `components.procurement.forecasting.reference_period_modal.upload_error.${key}`

type Translate = (key: string, options?: Record<string, string>) => string

type UploadErrorPattern = {
  pattern: RegExp
  key: string
  params: (match: RegExpMatchArray) => Record<string, string>
}

const noParams = () => ({})

const uploadErrorPatterns: UploadErrorPattern[] = [
  { pattern: /^CSV file is empty$/, key: 'empty', params: noParams },
  {
    pattern: /^Invalid headers\. Expected: timestamp_start;amount_kwh, Found: (.+)$/,
    key: 'invalid_headers',
    params: match => ({ found: match[1] }),
  },
  { pattern: /^CSV file contains no data rows$/, key: 'no_data_rows', params: noParams },
  {
    pattern: /^Custom reference base load upload supports ELECTRICITY and GAS$/,
    key: 'unsupported_energy_type',
    params: noParams,
  },
  {
    pattern: /^Row (\d+): Expected 2 columns$/,
    key: 'expected_two_columns',
    params: match => ({ row: match[1] }),
  },
  {
    pattern: /^Row (\d+): Invalid timestamp format: (.+)$/,
    key: 'invalid_timestamp',
    params: match => ({ row: match[1], value: match[2] }),
  },
  {
    pattern: /^Row (\d+): Invalid amount format: (.+)$/,
    key: 'invalid_amount',
    params: match => ({ row: match[1], value: match[2] }),
  },
  {
    pattern: /^Row (\d+): amount must not be negative: (.+)$/,
    key: 'negative_amount',
    params: match => ({ row: match[1], value: match[2] }),
  },
  {
    pattern: /^Row (\d+): Duplicate timestamp found: (.+)$/,
    key: 'duplicate_timestamp',
    params: match => ({ row: match[1], value: match[2] }),
  },
  {
    pattern: /^Row (\d+): timestamps must be in chronological order: (.+)$/,
    key: 'chronological_order',
    params: match => ({ row: match[1], value: match[2] }),
  },
  {
    pattern: /^Row (\d+): Each time must be on a 15-minute mark \(00, 15, 30, or 45\)\. Got: (.+)\.$/,
    key: 'quarter_hour_mark',
    params: match => ({ row: match[1], time: match[2] }),
  },
  {
    pattern: /^Row (\d+): Each time must be at the top of the hour\. Got: (.+)\.$/,
    key: 'top_of_hour',
    params: match => ({ row: match[1], time: match[2] }),
  },
  {
    pattern: /^The file is missing (\d+) 15-minute value in (\d+)\. The first missing time is (.+)\.$/,
    key: 'missing_quarter_hour_one',
    params: match => ({ count: match[1], year: match[2], time: match[3] }),
  },
  {
    pattern: /^The file is missing (\d+) 15-minute values in (\d+)\. The first missing time is (.+)\.$/,
    key: 'missing_quarter_hour_many',
    params: match => ({ count: match[1], year: match[2], time: match[3] }),
  },
  {
    pattern: /^The file is missing (\d+) hourly value in (\d+)\. The first missing time is (.+)\.$/,
    key: 'missing_hour_one',
    params: match => ({ count: match[1], year: match[2], time: match[3] }),
  },
  {
    pattern: /^The file is missing (\d+) hourly values in (\d+)\. The first missing time is (.+)\.$/,
    key: 'missing_hour_many',
    params: match => ({ count: match[1], year: match[2], time: match[3] }),
  },
  {
    pattern: /^(\d+) timestamp is outside (\d+)\. The first one is (.+)\.$/,
    key: 'outside_year_one',
    params: match => ({ count: match[1], year: match[2], time: match[3] }),
  },
  {
    pattern: /^(\d+) timestamps are outside (\d+)\. The first one is (.+)\.$/,
    key: 'outside_year_many',
    params: match => ({ count: match[1], year: match[2], time: match[3] }),
  },
]

const translateUploadErrorLine = (line: string, t: Translate) => {
  for (const entry of uploadErrorPatterns) {
    const match = line.match(entry.pattern)
    if (!match) continue
    const params = entry.params(match)
    return Object.keys(params).length ? t(uploadErrorKey(entry.key), params) : t(uploadErrorKey(entry.key))
  }

  return null
}

export const translateCustomReferenceBaseLoadUploadError = (message: string, t: Translate): string | null => {
  const lines = message
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)

  if (!lines.length) return null

  const translatedLines = lines.map(line => translateUploadErrorLine(line, t))
  if (translatedLines.some(line => line === null)) return null

  return translatedLines.join('\n')
}
